import { ChevronDown } from "lucide-react";
import type { CSSProperties, ComponentProps, HTMLAttributes, ReactNode, Ref } from "react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { DocumentReference } from "@/canvas/types/content";
import { cn } from "@/lib/utils";
import { DocumentCitationChip } from "./CitationChip";

type DocumentReferenceLike =
  | string
  | Pick<DocumentReference, "id" | "fileName" | "pageNumber" | "sourceFileId">;

function AnalysisListStatusLabel({ children }: { children: ReactNode }) {
  return <span className="text-ui-eyebrow">{children}</span>;
}

const defaultAnalysisListAccordionTitleClassName = "text-ui-body font-medium text-foreground";

interface AnalysisListHeaderProps {
  title: ReactNode;
  metrics?: ReactNode;
  trailing?: ReactNode;
  className?: string;
  titleGroupClassName?: string;
  metricsClassName?: string;
}

export function AnalysisListHeader({
  title,
  metrics,
  trailing,
  className,
  titleGroupClassName,
  metricsClassName,
}: AnalysisListHeaderProps) {
  return (
    <div className={cn("flex items-baseline justify-between gap-4 mb-3", className)}>
      <div className={cn("flex items-baseline gap-3 min-w-0", titleGroupClassName)}>
        <div className="min-w-0">{title}</div>
        {metrics ? (
          <div className={cn("flex items-center gap-3", metricsClassName)}>{metrics}</div>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}

interface AnalysisListMetricProps {
  icon: ReactNode;
  value: ReactNode;
  tooltip?: ReactNode;
  className?: string;
  valueClassName?: string;
}

export function AnalysisListMetric({
  icon,
  value,
  tooltip,
  className,
  valueClassName,
}: AnalysisListMetricProps) {
  const content = (
    <div
      className={cn(
        "flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity",
        className,
      )}
    >
      {icon}
      <span className={cn("text-sm font-tektur font-medium tabular-nums", valueClassName)}>
        {value}
      </span>
    </div>
  );

  if (!tooltip) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

interface AnalysisListStatusChipProps {
  label: ReactNode;
  className?: string;
}

export function AnalysisListStatusChip({ label, className }: AnalysisListStatusChipProps) {
  return (
    <span
      className={cn(
        "inline-flex h-8 max-w-40 items-center justify-center rounded-full border px-2.5 whitespace-nowrap",
        className,
      )}
    >
      <AnalysisListStatusLabel>{label}</AnalysisListStatusLabel>
    </span>
  );
}

interface AnalysisListStatusOption {
  value: string;
  label: ReactNode;
}

interface AnalysisListStatusSelectProps {
  value: string;
  displayLabel?: ReactNode;
  options: AnalysisListStatusOption[];
  onValueChange: (value: string) => void;
  className?: string;
}

export function AnalysisListStatusSelect({
  value,
  displayLabel,
  options,
  onValueChange,
  className,
}: AnalysisListStatusSelectProps) {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          className={cn(
            "inline-flex h-8 max-w-40 items-center rounded-full border whitespace-nowrap",
            "w-auto justify-center px-2.5 pr-2 shadow-none hover:bg-muted/20 [aria-expanded=true]:bg-muted/20 focus-visible:border-border/70 focus-visible:ring-2 focus-visible:ring-accent-heavy/10 focus-visible:ring-offset-0 [&>span]:w-auto [&>span]:min-w-0 [&>span]:truncate [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:opacity-55",
            className,
          )}
        >
          <SelectValue>
            <AnalysisListStatusLabel>
              {displayLabel ?? selectedOption?.label ?? value}
            </AnalysisListStatusLabel>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface AnalysisListEditableTitleProps {
  value: string;
  draftValue: string;
  isEditing: boolean;
  isOpen: boolean;
  readOnly?: boolean;
  onChange: (value: string) => void;
  onStartEditing: () => void;
  onCommit: () => void;
  onCancel: () => void;
  ariaLabel?: string;
  textClassName?: string;
  displayClassName?: string;
  editingClassName?: string;
}

export function AnalysisListEditableTitle({
  value,
  draftValue,
  isEditing,
  isOpen,
  readOnly = false,
  onChange,
  onStartEditing,
  onCommit,
  onCancel,
  ariaLabel,
  textClassName,
  displayClassName,
  editingClassName,
}: AnalysisListEditableTitleProps) {
  const canEditFromTitle = !readOnly && isOpen;

  if (isEditing && !readOnly) {
    return (
      <input
        value={draftValue}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onCommit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onCommit();
          } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
          event.stopPropagation();
        }}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        className={cn(
          "inline-block h-auto max-w-full align-bottom rounded-md border bg-card px-1 py-0.5 -mx-1 outline-none shadow-sm transition-[background-color,border-color,box-shadow,color]",
          "w-auto focus-visible:border-blueprint-heavy focus-visible:ring-3 focus-visible:ring-blueprint-heavy/20",
          defaultAnalysisListAccordionTitleClassName,
          textClassName,
          editingClassName,
        )}
        style={{ fieldSizing: "content" } as CSSProperties}
        aria-label={ariaLabel}
        autoFocus
      />
    );
  }

  return (
    <span
      className={cn(
        "inline-block h-auto max-w-full align-bottom rounded-md border border-transparent px-1 py-0.5 -mx-1 outline-none transition-[background-color,border-color,box-shadow,color]",
        defaultAnalysisListAccordionTitleClassName,
        textClassName,
        displayClassName,
        canEditFromTitle && "cursor-text hover:bg-muted/50",
      )}
      onClick={(event) => {
        if (!canEditFromTitle) {
          return;
        }

        event.stopPropagation();
        onStartEditing();
      }}
      onPointerDown={(event) => {
        if (!canEditFromTitle) {
          return;
        }

        event.stopPropagation();
      }}
    >
      {value}
    </span>
  );
}

interface AnalysisListEditableHeaderTitleProps {
  value: string;
  draftValue: string;
  isEditing: boolean;
  readOnly?: boolean;
  onChange: (value: string) => void;
  onStartEditing: () => void;
  onCommit: () => void;
  onCancel: () => void;
  displayClassName?: string;
  editingClassName?: string;
}

export function AnalysisListEditableHeaderTitle({
  value,
  draftValue,
  isEditing,
  readOnly = false,
  onChange,
  onStartEditing,
  onCommit,
  onCancel,
  displayClassName,
  editingClassName,
}: AnalysisListEditableHeaderTitleProps) {
  const isEditingValue = isEditing && !readOnly;
  const measuredValue = (isEditingValue ? draftValue : value) || " ";

  return (
    <h3 className={cn("text-ui-title-3 text-foreground whitespace-nowrap", displayClassName)}>
      <span
        className={cn(
          "relative inline-grid align-baseline rounded-md border px-1 py-0 -mx-1 transition-[background-color,border-color,box-shadow,color]",
          isEditingValue
            ? "border-border/60 bg-card shadow-sm focus-within:border-blueprint-heavy focus-within:ring-2 focus-within:ring-blueprint-heavy/15 focus-within:ring-offset-0"
            : "border-transparent",
          !isEditingValue && !readOnly && "cursor-text hover:bg-black/2",
        )}
        onClick={() => {
          if (!readOnly && !isEditingValue) {
            onStartEditing();
          }
        }}
      >
        {isEditingValue ? (
          <>
            <span
              aria-hidden="true"
              className={cn(
                "text-ui-title-3 text-foreground invisible col-start-1 row-start-1 whitespace-nowrap pr-2",
                editingClassName,
              )}
            >
              {measuredValue}
            </span>
            <input
              value={draftValue}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onCommit}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onCommit();
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  onCancel();
                }
              }}
              className={cn(
                "text-ui-title-3 text-foreground col-start-1 row-start-1 h-auto min-w-0 appearance-none bg-transparent pr-2 outline-none",
                editingClassName,
              )}
              style={{ fieldSizing: "content" } as CSSProperties}
              autoFocus
            />
          </>
        ) : (
          <span className="col-start-1 row-start-1">{value}</span>
        )}
      </span>
    </h3>
  );
}

interface AnalysisListAddButtonProps extends Omit<
  ComponentProps<typeof Button>,
  "variant" | "size"
> {
  children: ReactNode;
}

export function AnalysisListAddButton({
  children,
  className,
  ...props
}: AnalysisListAddButtonProps) {
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn(
        "h-10 w-full rounded-xl border-dashed border-border/55 bg-background/30 px-4 text-foreground/80 shadow-none hover:border-border/75 hover:bg-muted/15 hover:text-foreground",
        className,
      )}
      {...props}
    >
      <span className="inline-flex items-center justify-center gap-2">{children}</span>
    </Button>
  );
}

interface AnalysisListCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expandable: boolean;
  cardRef?: Ref<HTMLDivElement>;
  dragHandle?: ReactNode;
  leading?: ReactNode;
  title: ReactNode;
  trailing?: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
}

export function AnalysisListCard({
  open,
  onOpenChange,
  expandable,
  cardRef,
  dragHandle,
  leading,
  title,
  trailing,
  body,
  footer,
  className,
  headerClassName,
  bodyClassName,
  contentClassName,
  footerClassName,
}: AnalysisListCardProps) {
  const hasExpandedContent = Boolean(body) || Boolean(footer);
  const canExpand = expandable && hasExpandedContent;

  return (
    <div className="transition-all duration-200">
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <div
          ref={cardRef}
          className={cn(
            "group/analysis-list-card rounded-lg border bg-card transition-all duration-200",
            open && "shadow-sm border-border",
            className,
          )}
        >
          <div
            role={canExpand ? "button" : undefined}
            tabIndex={canExpand ? 0 : undefined}
            aria-expanded={canExpand ? open : undefined}
            onClick={() => {
              if (canExpand) onOpenChange(!open);
            }}
            onKeyDown={(event) => {
              if (!canExpand) return;

              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenChange(!open);
              }
            }}
            className={cn(
              "w-full text-left p-3 flex items-center gap-3 rounded-lg aria-expanded:bg-muted/60",
              open && "rounded-b-none",
              canExpand && "cursor-pointer hover:bg-muted/30 transition-colors",
              headerClassName,
            )}
          >
            {dragHandle}
            {leading}
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <div className="flex-1 min-w-0">{title}</div>
              {(trailing || canExpand) && (
                <div className="flex items-center gap-2 shrink-0">
                  {trailing}
                  {canExpand && (
                    <div className="h-8 w-8 shrink-0 flex items-center justify-center text-muted-foreground rounded-md hover:bg-ground-core transition-colors">
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          open && "rotate-180",
                        )}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {hasExpandedContent && (
            <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none">
              <div className={cn("p-3", bodyClassName)}>
                {body ? (
                  <div className={cn("flex flex-col gap-4 pl-7 max-w-[65ch]", contentClassName)}>
                    {body}
                  </div>
                ) : null}
                {footer ? (
                  <div
                    className={cn(
                      "flex items-center justify-between mt-2 pt-2 pl-7 border-t border-border/30",
                      footerClassName,
                    )}
                  >
                    {footer}
                  </div>
                ) : null}
              </div>
            </CollapsibleContent>
          )}
        </div>
      </Collapsible>
    </div>
  );
}

export function AnalysisListCardDragHandle({
  children,
  className,
  onClick,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn(
        "shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/20 hover:text-muted-foreground/80 transition-opacity opacity-0 group-hover/analysis-list-card:opacity-100 focus-within:opacity-100",
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      {children}
    </div>
  );
}

interface DocumentReferenceChipsProps {
  refs?: DocumentReferenceLike[] | null;
  className?: string;
}

export function DocumentReferenceChips({ refs, className }: DocumentReferenceChipsProps) {
  if (!refs?.length) {
    return null;
  }

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {refs.map((ref, index) => (
        <DocumentCitationChip
          key={typeof ref === "string" ? `${ref}-${index}` : (ref.id ?? `${ref.fileName}-${index}`)}
          fileName={typeof ref === "string" ? ref : ref.fileName}
          sourceFileId={typeof ref === "string" ? undefined : ref.sourceFileId}
          pageNumber={typeof ref === "string" ? undefined : ref.pageNumber}
        />
      ))}
    </div>
  );
}

export function replaceAnalysisListItems<TItem, TContent extends { items: TItem[] }>(
  content: TContent,
  items: TItem[],
): TContent {
  return {
    ...content,
    items,
  };
}
